import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useLocationSelectorContext } from './LocationSelectorContext.js';
import { folderToLocationSelectorNodeData } from './utils.js';
import { useQueryFolders } from '../../queries/useQueryFolders.js';
import { useInView, motion } from 'framer-motion';
import React__default from 'react';
import { styled, css } from 'styled-components';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { P } from '@fabric-app/ui/components/Typography';
import { Button } from '@fabric-app/ui/components/Button';
import { mediaHover } from '@fabric-app/ui/constants/styled-utils';
import { Flex } from '@fabric-app/ui/components/Flex';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';
import { framerAnimationFade } from '@fabric-app/ui/constants/framerAnimations';
import { AnimateChangeInHeight } from '@fabric-app/ui/components/animations/AnimateChangeInHeight';
import { IntersectionObserverAnchor } from '@fabric-app/ui/components/IntersectionObserver/IntersectionObserver';
import { ChevronUpIcon } from '@radix-ui/react-icons';
import { SidebarActionButton } from '@fabric-app/ui/components/button/ButtonExpandCollapse';

const NodeWrapper = styled.div.withConfig({
  displayName: "LocationSelectorNode__NodeWrapper",
  componentId: "sc-f688a391-0"
})([
  `border-top:1px solid `,
  `;height:59px;display:flex;align-items:center;gap:0.625rem;padding-left:4px;padding-right:10px;overflow:hidden;width:100%;`
], cssVar["color-border-secondary"]);
const cssNameButtonHighlightState = css([
  `background:rgba(`,
  `,0.1);box-shadow:none;color:`,
  `;svg,`,
  `{color:`,
  `;}`
], cssVar["color-app-primary-rgb"], cssVar["color-app-primary"], P, cssVar["color-app-primary-text"]);
const NameButton = styled(Button).attrs({
  variant: "transparent"
}).withConfig({
  displayName: "LocationSelectorNode__NameButton",
  componentId: "sc-f688a391-1"
})([
  `flex-grow:1;justify-content:flex-start;outline-width:1px;padding-left:1rem;padding-right:1rem;font-size:0.9375rem;color:`,
  `;border-radius:0.5rem;gap:1rem;overflow:hidden;flex-shrink:1;svg{color:`,
  `;}`,
  `{&:hover:enabled{`,
  ` background:rgba(`,
  `,0.04);&[data-node-selector-selected='true']{background:rgba(`,
  `,0.1);}}}&[data-node-selector-selected='true']{`,
  `}&:focus{outline:none;}&:disabled,&:disabled:hover{background:transparent;color:`,
  `;opacity:0.5;`,
  `,svg{color:`,
  `;}}`
], cssVar["color-text-primary"], cssVar["color-text-quaternary"], mediaHover, cssNameButtonHighlightState, cssVar["color-app-primary-rgb"], cssVar["color-app-primary-rgb"], cssNameButtonHighlightState, cssVar["color-text-placeholder"], P, cssVar["color-text-placeholder"]);
const CollapseButtonWrapper = styled(Flex).attrs({
  justifyContent: "center",
  alignItems: "center"
}).withConfig({
  displayName: "LocationSelectorNode__CollapseButtonWrapper",
  componentId: "sc-f688a391-2"
})([
  `height:40px;width:40px;`
]);
const defaultGetIsNodeSelectable = (node, cb) => !node.isReadOnly && node.canCreateChildren && (cb ? cb(node) : true);
const LocationSelectorNode = ({ data, level, prevNodePath }) => {
  const { selectedNode, onSelectNode, activeNodePath, getIsNodeSelectable } = useLocationSelectorContext();
  const isSelectable = defaultGetIsNodeSelectable(data, getIsNodeSelectable);
  const isDescendantSelected = !!activeNodePath?.slice(0, -1).find((item) => item.id === data.id);
  const isExpandedState = useBoolState(isDescendantSelected);
  const isExpanded = !!isDescendantSelected || isExpandedState.value;
  const containerRef = React__default.useRef(null);
  const isInView = useInView(containerRef);
  const { folders, fetchNextPage, hasNextPage } = useQueryFolders(data.id, {
    /**
    * do not fetch descendants if this folder is being moved
    */
    enabled: isSelectable && isInView,
    /**
    * don't expect folders to change often during the selections
    */
    staleTime: 1e3 * 60
  });
  const subfolders = folders.map(folderToLocationSelectorNodeData);
  const thisNodePath = [
    ...prevNodePath,
    data
  ];
  const isNodeSelected = selectedNode?.id === data.id;
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsxs(NodeWrapper, {
        ref: containerRef,
        as: motion.div,
        ...framerAnimationFade,
        style: {
          paddingLeft: `${level * 20 + 10}px`
        },
        children: [
          /* @__PURE__ */ jsxs(NameButton, {
            "data-node-selector-selected": isNodeSelected,
            "data-testid": `location-select-item-[${data.title}]`,
            role: "button",
            onClick: (e) => {
              if (isNodeSelected) {
                onSelectNode(null);
              } else {
                onSelectNode(data);
                e.currentTarget.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest"
                });
              }
            },
            disabled: !isSelectable,
            children: [
              data.icon,
              /* @__PURE__ */ jsx(P, {
                weight: 600,
                color: "primary",
                ellipsis: true,
                children: data.title
              })
            ]
          }),
          /* @__PURE__ */ jsx(CollapseButtonWrapper, {
            children: /* @__PURE__ */ jsx(SidebarActionButton, {
              onClick: isExpandedState.handleToggle,
              disabled: isDescendantSelected || subfolders.length === 0 || !isSelectable,
              children: /* @__PURE__ */ jsx(ChevronUpIcon, {
                style: {
                  height: 14,
                  width: 14,
                  transition: ".2s rotate",
                  rotate: isExpanded ? "0deg" : "180deg"
                }
              })
            })
          })
        ]
      }),
      /* @__PURE__ */ jsx(AnimateChangeInHeight, {
        duration: 0.02,
        children: isExpanded && /* @__PURE__ */ jsxs(Fragment, {
          children: [
            subfolders.map((node) => /* @__PURE__ */ jsx(LocationSelectorNode, {
              data: node,
              level: level + 1,
              prevNodePath: thisNodePath
            }, node.id)),
            hasNextPage && /* @__PURE__ */ jsx(IntersectionObserverAnchor, {
              onIntersect: fetchNextPage
            })
          ]
        })
      })
    ]
  });
};

export { LocationSelectorNode };
//# sourceMappingURL=LocationSelectorNode.js.map
